import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import styled from 'styled-components';
import theme from '../theme';
import { TerminalCard, TerminalButton, TerminalInput, TerminalTitle, TerminalText } from './shared/TerminalBox';
import { TerminalLabel, TerminalAlert, TerminalLoading } from './shared/TerminalForms';


const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 100vh;
  background: ${theme.colors.bg};
  color: ${theme.colors.text};
`;

const Title = styled(TerminalTitle)`
  margin-bottom: 2rem;
  font-size: 2rem;
  
  &::before {
    content: '> ';
    color: ${theme.colors.primary};
  }
`;

const SearchForm = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const InputGroup = styled.div`
  flex: 1;
  min-width: 200px;
`;

const StatsCard = styled(TerminalCard)`
  margin: 2rem auto;
  max-width: 1000px;
`;

const StatsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${theme.colors.border};
`;

const StatsTitle = styled.h2`
  font-family: ${theme.fonts.mono};
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: ${theme.colors.text};
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &::before {
    content: '[ ';
    color: ${theme.colors.primary};
  }
  
  &::after {
    content: ' ]';
    color: ${theme.colors.primary};
  }
`;

const StatsInfo = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${theme.colors.bgSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${theme.colors.text};
`;

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const NewsCard = styled(TerminalCard)`
  transition: all ${theme.transitions.normal};
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${theme.shadows.glowStrong};
  }
`;

const NewsSource = styled.div`
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  color: ${theme.colors.textSecondary};
  margin-bottom: 0.5rem;
  font-weight: 500;
  text-transform: uppercase;
  
  &::before {
    content: '[SOURCE] ';
    color: ${theme.colors.primary};
  }
`;

const NewsTitle = styled.h3`
  font-family: ${theme.fonts.mono};
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 0.75rem 0;
  color: ${theme.colors.text};
  line-height: 1.4;
`;

const NewsTimestamp = styled.div`
  font-family: ${theme.fonts.mono};
  font-size: 0.75rem;
  color: ${theme.colors.textMuted};
  margin-bottom: 0.75rem;
  
  &::before {
    content: '[TIME] ';
    color: ${theme.colors.primary};
  }
`;

const NewsContent = styled.p`
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  color: ${theme.colors.textSecondary};
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const NewsLink = styled.a`
  display: inline-block;
  padding: 0.5rem 1rem;
  background: transparent;
  border: 1px solid ${theme.colors.borderBright};
  color: ${theme.colors.text};
  text-decoration: none;
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  font-weight: 600;
  transition: all ${theme.transitions.fast};
  text-transform: uppercase;
  
  &:hover {
    background: ${theme.colors.overlayLight};
    box-shadow: ${theme.shadows.glow};
    border-color: ${theme.colors.primaryLight};
  }
`;

const SymbolsContainer = styled.div`
  margin-top: 0.75rem;
`;

const SymbolsTitle = styled.div`
  font-family: ${theme.fonts.mono};
  font-size: 0.75rem;
  font-weight: 600;
  color: ${theme.colors.textMuted};
  margin-bottom: 0.25rem;
  text-transform: uppercase;
`;

const SymbolsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
`;

const SymbolChip = styled.span`
  padding: 0.125rem 0.5rem;
  background: ${theme.colors.bgSecondary};
  border: 1px solid ${theme.colors.border};
  color: ${theme.colors.text};
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.fonts.mono};
  font-size: 0.75rem;
  font-weight: 500;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
`;

const PageButton = styled(TerminalButton)`
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.span`
  font-family: ${theme.fonts.mono};
  font-weight: 600;
  color: ${theme.colors.text};
  font-size: 0.875rem;
`;

const Recent24HourNews = () => {
  const [keywords, setKeywords] = useState('bitcoin');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newsData, setNewsData] = useState(null);

  const fetchNews = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/recent-news',
        {
          params: {
            keywords: keywords,
            page: page
          }
        }
      );

      setNewsData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'An error occurred while fetching news');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, [page]);

  const handleSearch = () => {
    setPage(1);
    fetchNews();
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
  };

  const truncateText = (text, maxLength = 200) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Container>
      <Title>Recent 24-Hour Market News</Title>

      <TerminalCard>
        <SearchForm>
          <InputGroup>
            <TerminalLabel htmlFor="keywords">Keywords</TerminalLabel>
            <TerminalInput
              id="keywords"
              type="text"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="bitcoin, tesla, apple"
            />
          </InputGroup>
          <TerminalButton onClick={handleSearch} disabled={loading}>
            {loading ? 'Searching...' : 'Search News'}
          </TerminalButton>
        </SearchForm>
      </TerminalCard>

      {error && (
        <TerminalAlert className="error" style={{ marginTop: '1rem' }}>
          {error}
        </TerminalAlert>
      )}

      {loading && (
        <TerminalLoading>
          Loading latest market news...
        </TerminalLoading>
      )}

      {newsData && (
        <>
          <StatsCard>
            <StatsHeader>
              <StatsTitle>News Summary</StatsTitle>
              <StatsInfo>
                <StatItem>
                  Total: {newsData.total_items || 0}
                </StatItem>
                <StatItem>
                  Page: {newsData.page || 1}
                </StatItem>
                <StatItem>
                  Updated: {newsData.last_update ? formatTimestamp(newsData.last_update) : 'N/A'}
                </StatItem>
              </StatsInfo>
            </StatsHeader>
          </StatsCard>

          <NewsGrid>
            {newsData.data && newsData.data
              .sort((a, b) => (b.published_at || 0) - (a.published_at || 0))
              .map((news, index) => (
              <NewsCard key={index}>
                <NewsSource>
                  {news.source || 'Unknown Source'}
                </NewsSource>
                <NewsTitle>
                  {news.title}
                </NewsTitle>
                <NewsTimestamp>
                  {news.published_at ? formatTimestamp(news.published_at) : 'Unknown time'}
                </NewsTimestamp>
                {news.content && (
                  <NewsContent>
                    {truncateText(news.content)}
                  </NewsContent>
                )}
                {news.related_symbols && news.related_symbols.length > 0 && (
                  <SymbolsContainer>
                    <SymbolsTitle>Related Symbols:</SymbolsTitle>
                    <SymbolsList>
                      {news.related_symbols.slice(0, 5).map((symbol, idx) => (
                        <SymbolChip key={idx}>
                          {symbol}
                        </SymbolChip>
                      ))}
                      {news.related_symbols.length > 5 && (
                        <SymbolChip>
                          +{news.related_symbols.length - 5} more
                        </SymbolChip>
                      )}
                    </SymbolsList>
                  </SymbolsContainer>
                )}
                <NewsLink
                  href={news.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Read Full Article
                </NewsLink>
              </NewsCard>
            ))}
          </NewsGrid>

          <Pagination>
            <PageButton
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1 || loading}
            >
              Previous
            </PageButton>
            <PageInfo>
              Page {page}
            </PageInfo>
            <PageButton
              onClick={() => setPage(page + 1)}
              disabled={!newsData.has_next || loading}
            >
              Next
            </PageButton>
          </Pagination>
        </>
      )}
    </Container>
  );
};

export default Recent24HourNews;
